// src/components/IPModule.jsx
// IP (inpatient) module — bed board across all wards, plus per-admission
// detail: add in-stay charges and discharge (generates the IP bill, frees
// the bed). Tracked independently of the visit-stage stepper since a stay
// can run for days after the originating visit's own stages finish.
//
// Discharge conversational v3 layout (2026-09-30, docs/HMS_CONVERSATIONAL_AI.md
// §6): "Discharge patient" now opens a dedicated, full-width two-step flow
// instead of an inline panel squeezed below the charges form — see
// AdmissionDetail's own `step` state below.
//
// Full-width detail panel (2026-10-01): selecting any bed now hides the
// ward/bed board entirely rather than narrowing it — the board is just a
// reference list once an admission is open, and every detail view (Case
// Sheet, charges, Discharge Summary) benefits from the full width, not only
// the Discharge Summary step. A "← Back to IP / Wards" button in the detail
// panel's own header (not any child screen's own, narrower "back" control)
// returns to the board. This also drops the old board-narrowing/resize
// mechanics (BOARD_WIDTH_KEY, the separator, narrowBoard) — board visibility
// no longer changes while an admission stays selected, so the tree-shape
// hazard that drove AdmissionDetail's own stable-three-child-structure
// comment below no longer applies to the board itself (only to
// AdmissionDetail's own children, untouched here).
//
// Discharge summary AI unification (2026-10-09, docs/HMS_V4_IP_CASE_SHEET_
// INDIA_DESIGN.md's Phase 5 implementation notes): the AI chat used to be
// embedded directly in this screen's 'summary' step. It now lives in
// exactly one place — the Case Sheet's Discharge tab — so there is a single
// canonical drafting surface instead of two independent copies of the same
// component. This screen's 'summary' step is a status view over that
// structured record (shared TanStack Query cache, no extra plumbing needed)
// plus a button that switches to the Case Sheet view and opens it there.
import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { PrintBillButton } from '../features/printing/PrintBillButton';
import { getWards, getAdmission, addIPCharge, removeIPCharge, dischargeAdmission, getOutstandingBills, openBillPdf, getRunningBill, openPatientDocument } from '../api';
import { useAuth } from '../hooks/useAuth';
import { useIsPhone } from '../hooks/useIsPhone';
import { useBillingSettings } from '../hooks/useBillingSettings';
import { type Admission } from './DischargeSummaryChat';
import NewLabOrderDialog, { type NewLabOrderDialogProps } from '../features/laboratory/NewLabOrderDialog';
import { ReferencesDrawer } from '../features/clinical-references/ReferencesDrawer';
import { CaseSheetWorkspace } from '../features/ip-case-sheet/CaseSheetWorkspace';
import { useDischargeSummary } from '../features/ip-case-sheet/hooks';
import { Card, StatCard } from './ui/Card';
import { BedCard } from './ui/BedCard';
import { Button } from './ui/Button';
import { Section } from './ui/Section';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/Table';
import { Badge } from './ui/Badge';
import { Textarea } from './ui/Input';
import { useClaimForAdmission, useAdmissionDeposits, useAdmissionPackages } from '../features/claims/hooks';
import { fetchIpRunningBill, generateProvisionalStatement, generateFinalBillExports, type IpRunningBill } from '../features/claims/dischargeApi';
import { computeSplitPreview, round2, type SplitPreviewResult } from '../features/claims/splitPreview';
import { CoveragePanel } from '../features/claims/CoveragePanel';
import { PackagePanel } from '../features/claims/PackagePanel';
import { useHideTopbar } from '../lib/topbarFocus';
import { SegmentedControl } from './ui/SegmentedControl';
import { DischargedAdmissionsList } from '../features/ip-admissions/DischargedAdmissionsList';
import { GenerateCaseSheetDialog } from '../features/ip-case-sheet/GenerateCaseSheetDialog';

interface Bed {
  id: number;
  bed_number: string | number;
  status: 'available' | 'occupied' | 'maintenance';
  patient_name?: string;
  patient_uhid?: string;
  admission_id?: number;
  /** Insurance Claims Phase 6 — a single `EXISTS` LATERAL join on the
   * backend's own board query (routes/ip.js's GET /wards), not a second
   * fetch; see that route's own comment. */
  has_active_package?: boolean;
  claim_mode?: string | null;
  claim_status?: string | null;
  claim_payer_code?: string | null;
  claim_auth_sent_at?: string | null;
}

interface Ward {
  id: number;
  name: string;
  ward_type: string;
  daily_rate: number;
  floor?: string;
  beds: Bed[];
}

// claim_head mirrors backend/utils/claimHeads.js's CHARGE_TYPE_HEAD_MAP —
// Insurance Claims Phase 2.1 made a claim head mandatory on every NEW
// ip_charges row (service_id or an explicit claim_head; see that file's own
// header for why the backend no longer infers one from charge_type by
// itself). This screen has no service-master picker yet (that's the Admin
// Panel's job, not built this pass), so a charge type here still implies
// exactly one head, same mapping the migration used to backfill old rows.
const CHARGE_TYPES = [
  { id:'doctor_visit', label:'Doctor visit', claim_head:'professional' },
  { id:'nursing',      label:'Nursing',      claim_head:'nursing' },
  { id:'procedure',    label:'Procedure',    claim_head:'procedure_misc' },
  { id:'consumable',   label:'Consumable',   claim_head:'consumables' },
  { id:'other',        label:'Other',        claim_head:'misc' },
];

interface IPModuleProps {
  /** Opens a patient's record in the same in-place Dialog Consultation/Visit
   * Workflow use (App.tsx's viewPatientRecord/viewRecordUhid), instead of a
   * new browser tab — a full navigation away is a heavier interruption than
   * a mid-task lookup like this warrants, and it's the one existing pattern
   * every other screen with this exact button already follows. */
  onViewPatientRecord?: (uhid: string) => void;
  /** Opens one admission straight away (Patient Records -> Admissions uses
   * this). `back` replaces the toolbar's "Back to IP / Wards" so the user
   * returns to where they came from. Consumed once (onOpenAdmissionConsumed). */
  openAdmission?: OpenAdmissionRequest | null;
  onOpenAdmissionConsumed?: () => void;
}

export interface OpenAdmissionRequest {
  id: number;
  uhid: string;
  /** False for a discharged admission: Case Sheet only, no Charges & discharge. */
  admitted: boolean;
  back?: { label: string; onBack: () => void };
}

// Where the open admission came from, which decides what the toolbar offers.
// A discharged admission has no bed on the board (so its UHID can't be read
// from there), can't take charges, lab orders or a second discharge, and
// goes back to the list it was opened from.
interface SelectionContext {
  uhid: string;
  admitted: boolean;
  back?: { label: string; onBack: () => void };
}

export default function IPModule({ onViewPatientRecord, openAdmission, onOpenAdmissionConsumed }: IPModuleProps) {
  const isPhone = useIsPhone();
  const { branchVersion, user } = useAuth();
  // Ordering a lab test for the patient in this bed. Same grant the
  // Consultation stage's 🧪 Lab order button and the server both check. The
  // order is charged to this admission (utils/labIpCharges.js), so a placed
  // order bumps chargesVersion to refresh the Charges & discharge list.
  const canOrderLab = !!user?.modules?.includes('lab:orders');
  // 📚 Hospital references (G1), answered for this admission's patient, de-identified.
  // Either reference source opens the drawer; each tab checks its own grant.
  const canAskReferences = !!user?.modules?.some((m: string) => m === 'clinical-references' || m === 'clinical-references:web');
  const [referencesOpen, setReferencesOpen] = useState(false);
  const [labPatient, setLabPatient] = useState<NewLabOrderDialogProps['patient']>(null);
  const [labNotice, setLabNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [chargesVersion, setChargesVersion] = useState(0);
  const [wards, setWards] = useState<Ward[] | null>(null);
  const [selectedAdmissionId, setSelectedAdmissionId] = useState<number | null>(null);
  const [selection, setSelection] = useState<SelectionContext | null>(null);
  // Bed board (open admissions) or the list of discharged ones.
  const [boardView, setBoardView] = useState<'beds' | 'discharged'>('beds');
  // An open admission has its own sticky toolbar + PatientHeader — hide App's top bar meanwhile.
  useHideTopbar(selectedAdmissionId != null);
  // Which half of an admission is on screen: the CLINICAL record (the Case
  // Sheet) or the OPERATIONAL one (in-stay charges and discharge, which this
  // component has always owned). They are genuinely different jobs done by
  // different people, so they are separate views rather than one merged panel
  // — and the clinical one leads, since that is what a ward round opens.
  const [detailView, setDetailView] = useState<'case-sheet' | 'billing'>('case-sheet');
  const [error, setError] = useState('');
  // Phase 5 unification: the AI discharge-summary assistant lives in exactly
  // one place now — CaseSheetWorkspace's Discharge tab — not a second copy
  // embedded here. "Charges & discharge" jumps there instead: flip the view
  // toggle above AND bump this nonce, which CaseSheetWorkspace watches to
  // switch itself to the Discharge tab and open the assistant. A nonce
  // (not a boolean) because the component stays mounted the whole time a
  // bed is selected, so the SAME request — open it again — has to produce a
  // change even if it was already open and closed once already.
  const [openDischargeNonce, setOpenDischargeNonce] = useState(0);
  const openCaseSheetDischarge = useCallback(() => {
    setDetailView('case-sheet');
    setOpenDischargeNonce((n) => n + 1);
  }, []);

  // Measured (not guessed) so the Case Sheet's own sticky PatientHeader can
  // stack directly beneath this toolbar once it, too, is pinned to the true
  // top — see the toolbar's own sticky styling below and PatientHeader's
  // `stickyOffsetPx` prop. A ResizeObserver, not a one-time measurement,
  // because the button row wraps to two lines at narrow widths (the
  // conditional Lab order / Patient record buttons), which changes its
  // height without unmounting anything.
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  useLayoutEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    // `entries[0].contentRect` excludes padding/border — this toolbar's own
    // vertical padding (added so it reads correctly once sticky-bled itself)
    // would then be silently dropped from the measurement, under-reporting
    // its real on-screen footprint. `getBoundingClientRect()` on the
    // observed element gives the full border-box instead.
    const ro = new ResizeObserver(() => setToolbarHeight(el.getBoundingClientRect().height));
    ro.observe(el);
    return () => ro.disconnect();
  }, [selectedAdmissionId]);

  const loadWards = useCallback(() => getWards().then(setWards).catch(e => setError(e instanceof Error ? e.message : String(e))), []);
  // A branch switch closes any open admission detail too — an admission id
  // selected under the old branch has no meaning (or belongs to a bed the
  // new branch doesn't even show) once the board itself has been refetched.
  useEffect(() => { loadWards(); setSelectedAdmissionId(null); setSelection(null); setDetailView('case-sheet'); }, [loadWards, branchVersion]);
  // Declared after the branch reset above so, on mount, the reset runs first
  // and this request survives it.
  useEffect(() => {
    if (!openAdmission) return;
    setSelectedAdmissionId(openAdmission.id);
    setSelection({ uhid: openAdmission.uhid, admitted: openAdmission.admitted, back: openAdmission.back });
    setDetailView('case-sheet');
    onOpenAdmissionConsumed?.();
  }, [openAdmission, onOpenAdmissionConsumed]);
  const closeAdmission = useCallback(() => { setSelectedAdmissionId(null); setSelection(null); }, []);
  const goBack = () => {
    const back = selection?.back;
    closeAdmission();
    back?.onBack();
  };
  const openBed = (admissionId: number) => { setSelectedAdmissionId(admissionId); setSelection(null); };
  const openDischarged = (row: { id: number; patient_uhid: string }) => {
    setSelectedAdmissionId(row.id);
    setSelection({ uhid: row.patient_uhid, admitted: false });
    setDetailView('case-sheet');
  };
  useEffect(() => { setLabPatient(null); setLabNotice(null); }, [selectedAdmissionId]);

  // The bed board carries no patient id/age/gender, so the admission is
  // fetched when the dialog is opened rather than on every bed selection.
  const openLabOrder = async () => {
    if (!selectedAdmissionId) return;
    setLabNotice(null);
    try {
      const ad: Admission = await getAdmission(selectedAdmissionId);
      setLabPatient({ id: ad.patient_id, name: ad.patient_name, uhid: ad.patient_uhid, age: ad.age, gender: ad.gender });
    } catch (e) { setLabNotice({ ok: false, text: `Could not open the lab order: ${e instanceof Error ? e.message : String(e)}` }); }
  };

  const occupancy = (wards || []).reduce((acc, w) => {
    w.beds.forEach(b => { acc.total++; if (b.status === 'occupied') acc.occupied++; });
    return acc;
  }, { total: 0, occupied: 0 });

  // Selecting any bed hides the board entirely (on every screen size, not
  // just phone) and gives the detail panel the full width — the board is
  // just a reference list once an admission is open, and the "← Back to
  // IP / Wards" button in the detail panel's own header (below) is how a
  // doctor returns to it.
  const showBoard  = !selectedAdmissionId;
  const showDetail = !!selectedAdmissionId;

  // The bed board already carries patient_uhid per occupied bed (BedCard
  // needs it too), so the "Patient record" button doesn't need its own
  // fetch — just look up which bed the selected admission is sitting in.
  const selectedPatientUhid = selection?.uhid ?? (wards || [])
    .flatMap(w => w.beds)
    .find(b => b.admission_id === selectedAdmissionId)?.patient_uhid;
  const isClosed = selection ? !selection.admitted : false;
  const showLabOrder = canOrderLab && !isClosed;

  const viewSwitch = (
    <div className="mb-4 w-full max-w-[320px]">
      <SegmentedControl<'beds' | 'discharged'>
        size="sm"
        value={boardView}
        onChange={setBoardView}
        options={[{ value: 'beds', label: 'Bed board' }, { value: 'discharged', label: 'Discharged' }]}
      />
    </div>
  );

  const boardPanel = boardView === 'discharged' ? (
    <div>
      {viewSwitch}
      <DischargedAdmissionsList onOpen={openDischarged} />
    </div>
  ) : (
    <div>
      {viewSwitch}
      <div className="mb-4 flex items-center gap-3.5">
        <StatCard className="px-4 py-2.5">
          <div className="text-xl font-bold">{occupancy.occupied} / {occupancy.total}</div>
          <div className="text-[11px] text-ink-muted">Beds occupied</div>
        </StatCard>
      </div>
      {error && (
        <div className="mb-3.5 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>
      )}

      {wards === null ? (
        <div className="p-10 text-center text-ink-muted">Loading wards…</div>
      ) : wards.length === 0 ? (
        <div className="p-10 text-center text-ink-muted/70">No wards configured yet — set them up in Admin Panel → Wards & Beds.</div>
      ) : (
        wards.map(w => (
          <Card key={w.id} className="mb-3.5 p-4">
            <div className="mb-3 flex items-baseline justify-between">
              <div>
                <span className="text-sm font-bold">{w.name}</span>
                <span className="ml-2 text-[11.5px] text-ink-muted">{w.ward_type} · ₹{w.daily_rate}/day{w.floor ? ` · ${w.floor} floor` : ''}</span>
              </div>
              <span className="text-[11.5px] text-ink-muted/70">{w.beds.filter(b=>b.status==='occupied').length}/{w.beds.length} occupied</span>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {w.beds.map(b => (
                <BedCard
                  key={b.id} bedNumber={b.bed_number} status={b.status}
                  patientName={b.patient_name} patientUhid={b.patient_uhid}
                  hasActivePackage={b.has_active_package}
                  claim={b.claim_status ? { mode: b.claim_mode ?? null, status: b.claim_status, payerCode: b.claim_payer_code ?? null, authSentAt: b.claim_auth_sent_at ?? null } : null}
                  selected={b.admission_id === selectedAdmissionId}
                  onClick={() => b.admission_id && openBed(b.admission_id)}
                />
              ))}
              {w.beds.length === 0 && <div className="text-xs text-ink-muted/70">No beds in this ward yet.</div>}
            </div>
          </Card>
        ))
      )}
    </div>
  );

  const detailPanel = selectedAdmissionId && showDetail && (
    <div>
      {/* Sticky and bled to the true top for the same reason PatientHeader.tsx
          is (see its own header comment for the full mechanism): App.tsx's
          shared page wrapper puts `padding: 20px 24px` directly on the
          scrolling element, so a `position:sticky` child only ever sticks at
          that padding edge unless its own `top`/margin cancel it out exactly
          (-20px top, -20px/-24px margin here). z-index above PatientHeader's
          (20) so this toolbar visually sits ON TOP of the stack it's the
          first row of. `toolbarRef`/`toolbarHeight` measure this row's real
          rendered height (it wraps to two lines at narrow widths) and feed
          it to PatientHeader as `stickyOffsetPx`, so that header's own sticky
          point lands exactly below this one instead of overlapping it. */}
      <div ref={toolbarRef} style={{
        display:'flex', alignItems:'center', gap:6, flexWrap:'wrap',
        // App.tsx pads the scroll container 14px on a phone, 20px/24px above —
        // the bleed has to match or the row overflows sideways by the difference.
        position:'sticky', top: isPhone ? -14 : -20, zIndex:21,
        marginTop: isPhone ? -14 : -20, marginLeft: isPhone ? -14 : -24, marginRight: isPhone ? -14 : -24, marginBottom:12,
        padding: isPhone ? '10px 14px 8px' : '20px 24px 10px', background:'var(--color-card, #fff)', borderBottom:'1px solid var(--color-border, #E5E7EB)',
      }}>
        <button
          onClick={goBack}
          title={selection?.back ? selection.back.label : isClosed ? 'Back to the discharged list' : 'Back to the ward/bed board'}
          style={{
            padding: isPhone ? '5px 9px' : '5px 12px', borderRadius:'var(--radius)', fontSize:12.5, fontWeight:600,
            cursor:'pointer', border:'1px solid var(--color-border, #E5E7EB)', background:'transparent', color:'#6B7280',
          }}>
          {selection?.back
            ? `← ${selection.back.label}`
            : isClosed
              ? (isPhone ? '← Discharged' : '← Back to discharged list')
              : (isPhone ? '← Wards' : '← Back to IP / Wards')}
        </button>
        {/* A discharged admission is its Case Sheet only: its bill is final
            (Billing / Patient Records -> Billing), and nothing can be charged
            to or discharged from a closed stay. */}
        {!isClosed && ([['case-sheet','Case sheet'],['billing','Charges & discharge']] as const).map(([id,label]) => (
          <button key={id} onClick={() => setDetailView(id)}
            style={{
              padding: isPhone ? '5px 9px' : '5px 12px', borderRadius:'var(--radius)', fontSize:12.5, fontWeight:600,
              cursor:'pointer',
              border: detailView === id ? '1px solid var(--teal)' : '1px solid var(--color-border, #E5E7EB)',
              background: detailView === id ? 'rgba(11,95,214,.08)' : 'transparent',
              color: detailView === id ? 'var(--teal)' : '#6B7280',
            }}>
            {isPhone && id === 'billing' ? 'Charges' : label}
          </button>
        ))}
        {showLabOrder && (
          <button
            onClick={openLabOrder}
            title="Order laboratory tests for this patient — charged to this admission"
            aria-label="Lab order"
            style={{
              marginLeft:'auto', padding: isPhone ? '5px 9px' : '5px 12px', borderRadius:'var(--radius)', fontSize:12.5, fontWeight:600,
              cursor:'pointer', border:'1px solid var(--color-border, #E5E7EB)', background:'transparent', color:'#6B7280',
            }}>
            {isPhone ? '🧪' : '🧪 Lab order'}
          </button>
        )}
        {canAskReferences && selectedAdmissionId != null && (
          <button
            onClick={() => setReferencesOpen(true)}
            title="Ask the hospital's approved clinical references, for this patient"
            aria-label="References"
            style={{
              marginLeft: showLabOrder ? 0 : 'auto', padding: isPhone ? '5px 9px' : '5px 12px', borderRadius:'var(--radius)', fontSize:12.5, fontWeight:600,
              cursor:'pointer', border:'1px solid var(--color-border, #E5E7EB)', background:'transparent', color:'#6B7280',
            }}>
            {isPhone ? '📚' : '📚 References'}
          </button>
        )}
        {selectedPatientUhid && onViewPatientRecord && (
          <button
            onClick={() => onViewPatientRecord(selectedPatientUhid)}
            title="View this patient's full record"
            aria-label="Patient record"
            style={{
              marginLeft: showLabOrder || (canAskReferences && selectedAdmissionId != null) ? 0 : 'auto', padding: isPhone ? '5px 9px' : '5px 12px', borderRadius:'var(--radius)', fontSize:12.5, fontWeight:600,
              cursor:'pointer', border:'1px solid var(--color-border, #E5E7EB)', background:'transparent', color:'#6B7280',
            }}>
            {isPhone ? '🗂' : '🗂 Patient record →'}
          </button>
        )}
      </div>
      {labNotice && (
        <div className={`mb-3 rounded-[var(--radius)] px-3 py-2 text-xs ${labNotice.ok ? 'bg-success-soft text-success-ink' : 'bg-danger-soft text-danger-ink'}`}>{labNotice.text}</div>
      )}
      {canAskReferences && (
        <ReferencesDrawer open={referencesOpen} onOpenChange={setReferencesOpen} admissionId={selectedAdmissionId} />
      )}
      {showLabOrder && (
        <NewLabOrderDialog
          open={!!labPatient}
          onClose={() => setLabPatient(null)}
          patient={labPatient}
          admissionId={selectedAdmissionId}
          onCreated={() => {
            setChargesVersion(v => v + 1);
            setLabNotice({ ok: true, text: 'Lab order placed — its charge has been added to this admission (see Charges & discharge).' });
          }}
        />
      )}
      {/* Both views stay mounted — switching tabs must never lose an
          in-progress discharge summary draft (or anything else typed
          into Charges & discharge) the way a conditional unmount would.
          Keyed on the admission id, not the tab: switching BEDS is a
          different patient's record and should start clean; switching
          TABS on the same admission must not. */}
      <div key={`case-sheet-${selectedAdmissionId}`} hidden={detailView !== 'case-sheet'}>
        <CaseSheetWorkspace admissionId={selectedAdmissionId}
          onClose={goBack} openDischargeOn={openDischargeNonce}
          stickyOffsetPx={toolbarHeight} />
      </div>
      {!isClosed && <div key={`billing-${selectedAdmissionId}`} hidden={detailView !== 'billing'}>
        <AdmissionDetail admissionId={selectedAdmissionId} onClose={goBack}
          onChanged={loadWards} isPhone={isPhone} onOpenCaseSheetDischarge={openCaseSheetDischarge}
          chargesVersion={chargesVersion} />
      </div>}
    </div>
  );

  return (
    <div className="fade-in" style={{ display: 'flex', gap: 16 }}>
      {showBoard && <div className="min-w-0" style={{ width: '100%' }}>{boardPanel}</div>}
      {detailPanel && <div className="min-w-0 flex-1" style={{ width: '100%' }}>{detailPanel}</div>}
    </div>
  );
}

interface OutstandingBill {
  id: number;
  bill_number: string;
  bill_type: string;
  total_amount: string | number;
  /** What the patient still owes on this bill (GET /api/billing/outstanding) —
   *  less than total_amount once part-paid or split with an insurer. */
  patient_balance?: string | number;
  sale_channel?: string | null;
  admission_id?: number | null;
}

/** What is still owed on a bill, falling back to its total for an older server. */
const billDue = (b: OutstandingBill) => parseFloat(String(b.patient_balance ?? b.total_amount)) || 0;

interface DischargeResult {
  bill: { id: number; bill_number: string; total_amount: string | number };
  settled_dues_total: number;
  grand_total: number;
  admission: { dues_override_reason?: string; dues_override_by?: string };
}

interface AdmissionDetailProps {
  admissionId: number;
  onClose: () => void;
  onChanged?: () => void;
  isPhone: boolean;
  onOpenCaseSheetDischarge: () => void;
  /** Bumped when something outside this panel (a lab order) adds a charge. */
  chargesVersion?: number;
}

// The discharge flow is two sequential steps, not one combined panel:
//   'closed'  — the normal charges-so-far view (unchanged), plus the button
//               that starts discharge.
//   'summary' — a status view of the structured discharge summary (read via
//               useDischargeSummary, shared cache with the Case Sheet) plus
//               a button into the Case Sheet's Discharge tab, where the AI
//               assistant now lives (Phase 5 unification — it used to be
//               embedded here directly; see docs/HMS_V4_IP_CASE_SHEET_INDIA_
//               DESIGN.md's Phase 5 implementation notes for why that moved
//               rather than staying duplicated in two places). A collapsible
//               plain-textarea fallback covers completing discharge with no
//               structured record at all.
//   'payment' — reached once a summary exists (structured, or the plain
//               fallback): charges so far (view/add one more), running
//               total, outstanding dues, payment mode, and the final
//               confirm-discharge-and-bill action.
type DischargeStep = 'closed' | 'summary' | 'payment';

function AdmissionDetail({ admissionId, onClose, onChanged, isPhone, onOpenCaseSheetDischarge, chargesVersion = 0 }: AdmissionDetailProps) {
  const { gstEnabled, gstPercentageOptions } = useBillingSettings();
  const [ad, setAd] = useState<Admission | null>(null);
  const [error, setError] = useState('');
  // Read-only here — drafting/signing happens in the Case Sheet's Discharge
  // tab (onOpenCaseSheetDischarge below). Shares its cache with that tab's
  // useDischargeSummary, so this status reflects what was just signed there
  // without this component doing anything itself to refresh it.
  const { data: structuredSummary } = useDischargeSummary(admissionId);
  const [chargeType, setChargeType] = useState('doctor_visit');
  const [chargeDesc, setChargeDesc] = useState('');
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeGst, setChargeGst] = useState('0');
  const [chargeDiscount, setChargeDiscount] = useState('0');
  const [roomRentGst, setRoomRentGst] = useState('0');

  // If the admin removes a percentage that's currently selected in this
  // still-open form, fall back to the first configured option rather than
  // silently keeping a value the dropdown no longer offers.
  useEffect(() => {
    const options = gstPercentageOptions.map(String);
    if (!options.length) return;
    if (!options.includes(chargeGst)) setChargeGst(options[0] as string);
    if (!options.includes(roomRentGst)) setRoomRentGst(options[0] as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstPercentageOptions]);
  const [addingCharge, setAddingCharge] = useState(false);
  const [step, setStep] = useState<DischargeStep>('closed');
  // Discharge-complete screen: the Generate case sheet dialog.
  const [caseSheetOpen, setCaseSheetOpen] = useState(false);
  const [dischargeSummary, setDischargeSummary] = useState('');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [discharging, setDischarging] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [result, setResult] = useState<DischargeResult | null>(null);
  const [outstanding, setOutstanding] = useState<OutstandingBill[] | null>(null); // null=not checked, []=clear, [...]=dues
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const { user } = useAuth();

  // Insurance Claims Phase 4.5.5 (wireframe S5) — the cashless/scheme/
  // corporate claim gating discharge, if any. Reimbursement is excluded
  // (same as the server's own gate in routes/ip.js) — it pays in full like
  // self-pay and never blocks discharge.
  const claimForAdmission = useClaimForAdmission(admissionId);
  const activeClaim = claimForAdmission.data && claimForAdmission.data.mode !== 'reimbursement'
    && !['cancelled', 'closed'].includes(claimForAdmission.data.status) ? claimForAdmission.data : null;
  const depositsQuery = useAdmissionDeposits(admissionId);
  const packagesQuery = useAdmissionPackages(admissionId);
  const activePackage = (packagesQuery.data ?? []).find((p) => p.status === 'active') ?? null;
  const [showPackagePanel, setShowPackagePanel] = useState(false);
  const unappliedDepositsTotal = (depositsQuery.data ?? [])
    .filter((d) => d.adjusted_bill_id == null)
    .reduce((s, d) => s + parseFloat(d.amount), 0);
  const [paymentRunningBill, setPaymentRunningBill] = useState<IpRunningBill | null>(null);
  const [splitPreview, setSplitPreview] = useState<SplitPreviewResult | null>(null);
  const [claimOverrideOpen, setClaimOverrideOpen] = useState(false);
  const [claimOverrideReason, setClaimOverrideReason] = useState('');
  const [printingBill, setPrintingBill] = useState(false);
  const [showCoveragePanel, setShowCoveragePanel] = useState(false);

  const load = useCallback(() => getAdmission(admissionId).then(setAd).catch(e => setError(e instanceof Error ? e.message : String(e))), [admissionId]);
  useEffect(() => { load(); }, [load, chargesVersion]);

  // Insurance Claims Phase 2.8 — the server's live, head-wise running bill
  // (utils/finalBill.js#buildStatement), shown as a chip alongside the
  // existing client-side room-rent preview above. Recomputed whenever the
  // charges list changes; never cached, same reasoning as the server side.
  const [runningBillStatement, setRunningBillStatement] = useState<{ net: number } | null>(null);
  useEffect(() => {
    getRunningBill(admissionId).then(setRunningBillStatement).catch(() => setRunningBillStatement(null));
  }, [admissionId, chargesVersion, ad]);

  const startDischarge = () => setStep('summary');

  const startPaymentStep = async () => {
    setStep('payment'); setOutstanding(null); setOverrideOpen(false); setOverrideReason('');
    setClaimOverrideOpen(false); setClaimOverrideReason(''); setPaymentRunningBill(null);
    if (!ad) return;
    try { setOutstanding(await getOutstandingBills(ad.patient_id)); } catch (e) { setError(e instanceof Error ? e.message : String(e)); setOutstanding([]); }
    if (activeClaim) {
      try { setPaymentRunningBill(await fetchIpRunningBill(admissionId)); }
      catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    }
  };
  const outstandingTotal = round2((outstanding || []).reduce((s, b) => s + billDue(b), 0));

  const printMainAndBreakupBill = async () => {
    setPrintingBill(true); setError('');
    try {
      await generateProvisionalStatement(admissionId);
      const { main_document_id, breakup_document_id } = await generateFinalBillExports(admissionId);
      if (ad) {
        await openPatientDocument(ad.patient_uhid, main_document_id, { inline: true });
        await openPatientDocument(ad.patient_uhid, breakup_document_id, { inline: true });
      }
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setPrintingBill(false);
  };

  const days = ad ? Math.max(1, Math.ceil((Date.now() - new Date(ad.admitted_at).getTime()) / 86400000)) : 0;
  const roomEstimate = ad ? ad.daily_rate * days : 0;
  const chargesTotal = ad ? ad.charges.reduce((s,c) => s + parseFloat(String(c.amount)) * (c.quantity||1), 0) : 0;
  const runningSubtotal = roomEstimate + chargesTotal;
  // Same calculation shape as every other GST line item in the app — this
  // is a live preview only (the actual bill is computed server-side at
  // discharge, same inputs: room rent uses roomRentGst, each ad-hoc charge
  // uses its own stored gst_percent from when it was added).
  const runningGst = ad && gstEnabled
    ? roomEstimate * ((parseFloat(roomRentGst) || 0) / 100) +
      ad.charges.reduce((s, c) => s + parseFloat(String(c.amount)) * (c.quantity || 1) * ((parseFloat(String(c.gst_percent || 0))) / 100), 0)
    : 0;
  const runningTotal = runningSubtotal + runningGst;

  // The running bill fetched in startPaymentStep is the SERVER's view as of
  // entering this step — it never includes the room-rent line, because
  // discharge itself is what inserts that ip_charges row (see routes/ip.js's
  // discharge route). The split preview must still account for it (the
  // server's own split, computed moments later at the actual discharge,
  // certainly will), or `collect_amount` sent below would always undershoot
  // and trip the server's echo-amount guard. So it's added here as a
  // synthetic 'room' head — same claim_head the discharge route itself uses
  // for this line — recomputed live as the room-rent GST% selector changes,
  // same as the existing client-side runningTotal preview above.
  useEffect(() => {
    if (!activeClaim || !paymentRunningBill) { setSplitPreview(null); return; }
    const roomGstAmount = gstEnabled ? roomEstimate * ((parseFloat(roomRentGst) || 0) / 100) : 0;
    const heads = [...paymentRunningBill.heads, { head: 'room', net: round2(roomEstimate + roomGstAmount) }];
    const approved = activeClaim.final_approved != null ? parseFloat(String(activeClaim.final_approved)) : null;
    setSplitPreview(computeSplitPreview(heads, approved, activeClaim.final_approval_deductions ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClaim?.final_approved, activeClaim?.final_approval_deductions, paymentRunningBill, roomEstimate, roomRentGst, gstEnabled]);

  // The claim gate: discharge needs activeClaim.status === 'final_approved',
  // or an Admin override with a reason (same rule routes/ip.js enforces
  // server-side — this is the UI's pre-flight, the server's check is the
  // real one). A reimbursement/no-claim admission has nothing to gate.
  const claimBlocksDischarge = !!activeClaim && activeClaim.status !== 'final_approved';
  const claimOverrideReady = claimOverrideOpen && claimOverrideReason.trim().length > 0;
  const patientShareFromClaim = splitPreview?.patientShare ?? null;
  const toCollectFromPatient = activeClaim && patientShareFromClaim != null
    ? Math.max(0, round2(patientShareFromClaim - unappliedDepositsTotal))
    : null;

  const addCharge = async () => {
    if (!chargeDesc.trim() || !chargeAmount) return;
    setAddingCharge(true); setError('');
    try {
      await addIPCharge(admissionId, {
        charge_type: chargeType, description: chargeDesc.trim(), amount: parseFloat(chargeAmount),
        gst_percent: gstEnabled ? (parseFloat(chargeGst) || 0) : 0,
        discount_percent: parseFloat(chargeDiscount) || 0,
        claim_head: CHARGE_TYPES.find(t => t.id === chargeType)?.claim_head || 'misc',
      });
      setChargeDesc(''); setChargeAmount(''); setChargeGst('0'); setChargeDiscount('0');
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setAddingCharge(false);
  };

  const removeCharge = async (chargeId: number) => {
    try { await removeIPCharge(admissionId, chargeId); await load(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const discharge = async (opts: { settle?: boolean; override?: boolean } = {}) => {
    setDischarging(true); setError('');
    try {
      const res = await dischargeAdmission(admissionId, {
        discharge_summary: dischargeSummary.trim() || null, payment_mode: paymentMode,
        settle_outstanding: opts.settle || undefined, override_dues_reason: opts.override ? overrideReason.trim() : undefined,
        room_rent_gst_percent: gstEnabled ? (parseFloat(roomRentGst) || 0) : 0,
        // Insurance Claims Phase 4.5.5 (§5.5) — only sent for a cashless/
        // scheme/corporate admission; a self-pay/reimbursement discharge
        // sends neither, byte-for-byte the pre-Phase-3 request shape.
        collect_amount: activeClaim && toCollectFromPatient != null ? toCollectFromPatient : undefined,
        claim_override_reason: claimOverrideReady ? claimOverrideReason.trim() : undefined,
      });
      setResult(res);
      onChanged?.();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setDischarging(false);
  };

  if (!ad) return <div className="card" style={{ padding:16 }}>Loading…</div>;

  if (result) {
    return (
      <div className="card" style={{ padding:16 }}>
        <div style={{ fontSize:32, textAlign:'center', marginBottom:8 }}>✅</div>
        <div style={{ fontWeight:700, textAlign:'center', marginBottom:4 }}>{ad.patient_name} discharged</div>
        <div style={{ fontSize:13, color:'#6B7280', textAlign:'center', marginBottom:14 }}>Bed {ad.bed_number} is now free.</div>
        <div style={{ padding:'10px 12px', background:'#F9FAFB', borderRadius:'var(--radius)', fontSize:13, marginBottom:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}><span>IP bill</span><b>{result.bill.bill_number}</b></div>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}><span>IP charges</span><span>₹{parseFloat(String(result.bill.total_amount)).toFixed(2)}</span></div>
          {result.settled_dues_total > 0 && (
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}><span>Outstanding dues settled</span><span>₹{result.settled_dues_total.toFixed(2)}</span></div>
          )}
          <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, paddingTop:6, borderTop:'1px dashed #E5E7EB' }}><span>Grand total collected</span><span>₹{result.grand_total.toFixed(2)}</span></div>
        </div>
        {result.admission.dues_override_reason && (
          <div style={{ marginBottom:14, padding:'8px 12px', background:'#FEF3C7', color:'#92400E', borderRadius:'var(--radius)', fontSize:11.5 }}>
            ⚠ Discharged with dues outstanding — overridden by {result.admission.dues_override_by}: "{result.admission.dues_override_reason}"
          </div>
        )}
        <div className="mb-3.5 rounded-[var(--radius)] border border-border bg-muted px-3 py-2.5 text-[12.5px]">
          <div className="mb-1 font-semibold">Case sheet</div>
          <div className="mb-2 text-ink-muted">
            Generate the MRD, insurance or patient copy now. It stays available later under IP → Discharged
            and Patient Records → Admissions.
          </div>
          <button className="btn-primary" style={{ padding:'6px 14px', fontSize:12.5 }} onClick={() => setCaseSheetOpen(true)}>
            📄 Generate case sheet
          </button>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn-ghost" style={{ flex:1 }} disabled={pdfLoading} onClick={async()=>{ setPdfLoading(true); try{ await openBillPdf(result.bill.id); }catch(e){setError(e instanceof Error ? e.message : String(e));} setPdfLoading(false); }}>
            {pdfLoading ? '…' : '📄 View invoice PDF'}
          </button>
          <PrintBillButton billId={result.bill.id} size="md" />
          <button className="btn-ghost" style={{ flex:1 }} onClick={onClose}>Close</button>
        </div>
        <GenerateCaseSheetDialog admissionId={admissionId} uhid={ad.patient_uhid}
          open={caseSheetOpen} onOpenChange={setCaseSheetOpen} />
      </div>
    );
  }

  // Step 1 of discharge. Phase 5 unification: the AI assistant itself no
  // longer lives here — it's embedded exactly once, in the Case Sheet's
  // Discharge tab (DischargeSummarySection.tsx), reached via
  // onOpenCaseSheetDischarge. This screen just shows that structured
  // record's current state (read-only — useDischargeSummary above shares
  // its cache with that tab, so this reflects what was signed there with no
  // extra plumbing) plus a minimal no-AI fallback for the rare case where
  // staff just need to type something to complete the discharge without
  // ever having drafted a structured summary.
  if (step === 'summary') {
    const hasStructured = !!structuredSummary;
    const canContinue = hasStructured || dischargeSummary.trim().length > 0;
    return (
      <div className="flex flex-col gap-3" style={{ minHeight: 520 }}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div style={{ fontWeight:700, fontSize:15 }}>{ad.patient_name} — Discharge summary</div>
            <div style={{ fontSize:12, color:'#6B7280' }}>{ad.patient_uhid} · {ad.ward_name}, bed {ad.bed_number}</div>
          </div>
          <button className="btn-ghost" style={{ padding:'5px 12px', fontSize:12 }} onClick={() => setStep('closed')}>← Back to charges</button>
        </div>
        {error && <div style={{ padding:'8px 12px', background:'#FEE2E2', color:'#991B1B', borderRadius:'var(--radius)', fontSize:12 }}>{error}</div>}

        <div style={{ padding:'14px 16px', background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:'var(--radius)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom: hasStructured ? 8 : 0 }}>
            <span style={{
              padding:'2px 10px', borderRadius:999, fontSize:11, fontWeight:700,
              background: structuredSummary?.status === 'signed' ? '#D1FAE5' : hasStructured ? '#FEF3C7' : '#F3F4F6',
              color: structuredSummary?.status === 'signed' ? '#065F46' : hasStructured ? '#92400E' : '#6B7280',
            }}>
              {structuredSummary?.status === 'signed' ? 'Signed' : hasStructured ? 'Draft' : 'Not started'}
            </span>
            {structuredSummary?.source === 'ai_assisted' && (
              <span style={{ fontSize:11, color:'#6B7280' }}>AI-assisted</span>
            )}
          </div>
          {structuredSummary?.reason_for_admission && (
            <div style={{ fontSize:12.5, marginBottom:4 }}><b>Reason:</b> {structuredSummary.reason_for_admission}</div>
          )}
          {structuredSummary?.hospital_course && (
            <div style={{ fontSize:12.5, marginBottom:10 }}><b>Course:</b> {structuredSummary.hospital_course}</div>
          )}
          <button className="btn-primary" style={{ padding:'7px 16px', fontSize:12.5 }} onClick={onOpenCaseSheetDischarge}>
            🤖 {hasStructured ? 'Review / continue' : 'Draft'} in Case Sheet →
          </button>
        </div>

        <details>
          <summary style={{ cursor:'pointer', fontSize:12, color:'#6B7280' }}>
            Or write plain text here instead (no AI — completes discharge without a structured record)
          </summary>
          <textarea
            className="input" style={{ width:'100%', minHeight:100, marginTop:8, fontSize:12.5 }}
            value={dischargeSummary} onChange={(e) => setDischargeSummary(e.target.value)}
            placeholder="Discharge summary text…"
          />
        </details>

        {canContinue && (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#F0FDF4', border:'1px solid #A7F3D0', borderRadius:'var(--radius)', fontSize:12.5, color:'#065F46' }}>
            <span style={{ flex:1 }}>✓ Ready to continue.</span>
            <button className="btn-primary" style={{ padding:'6px 14px', fontSize:12.5 }} onClick={startPaymentStep}>
              Continue to charges & payment →
            </button>
          </div>
        )}
      </div>
    );
  }

  // Step 2 of discharge: charges (final review, one last add if needed),
  // outstanding dues, payment mode, and the actual discharge+bill action.
  if (step === 'payment') {
    return (
      <div className="card" style={{ padding:16, alignSelf:'start' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
          <div style={{ fontWeight:700, fontSize:15 }}>{ad.patient_name} — Charges & payment</div>
          <button className="btn-ghost" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => setStep('summary')}>← Back to summary</button>
        </div>
        <div style={{ fontSize:12, color:'#6B7280', marginBottom:10 }}>{ad.patient_uhid} · {ad.ward_name} · Bed {ad.bed_number}</div>

        {error && <div style={{ marginBottom:10, padding:'8px 12px', background:'#FEE2E2', color:'#991B1B', borderRadius:'var(--radius)', fontSize:12 }}>{error}</div>}

        <div style={{ padding:'6px 10px', background:'#F0FDF4', border:'1px solid #A7F3D0', borderRadius:'var(--radius)', fontSize:11.5, color:'#065F46', marginBottom:12 }}>
          ✓ Discharge summary ready
        </div>

        <ChargesList ad={ad} days={days} roomEstimate={roomEstimate} runningSubtotal={runningSubtotal} runningGst={runningGst}
          runningTotal={runningTotal} runningBillStatement={runningBillStatement} gstEnabled={gstEnabled} gstPercentageOptions={gstPercentageOptions}
          roomRentGst={roomRentGst} setRoomRentGst={setRoomRentGst} removeCharge={removeCharge} />

        <div style={{ display:'grid', gridTemplateColumns:'110px 1fr', gap:6, marginBottom:6 }}>
          <select value={chargeType} onChange={e=>setChargeType(e.target.value)} style={{ fontSize:12 }}>
            {CHARGE_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <input placeholder="Description" value={chargeDesc} onChange={e=>setChargeDesc(e.target.value)} style={{ fontSize:12 }} />
        </div>
        {/* Wraps instead of squeezing: on a phone the amount box was crushed
            to zero width and "+ Add charge" ran past the card edge. */}
        <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:6, marginBottom:14 }}>
          <input type="number" placeholder="Amount ₹" value={chargeAmount} onChange={e=>setChargeAmount(e.target.value)} style={{ fontSize:12, flex:'1 1 110px', minWidth:0, width:'auto' }} />
          <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
            <span style={{ fontSize:10, fontWeight:600, color:'#9CA3AF' }}>Disc</span>
            <input
              type="number" min="0" max="100" value={chargeDiscount} onChange={e=>setChargeDiscount(e.target.value)}
              style={{ fontSize:12, width:50 }} title="Discount % — a courtesy discount on this specific charge line"
            />
          </div>
          {gstEnabled && (
            <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
              <span style={{ fontSize:10, fontWeight:600, color:'#9CA3AF' }}>GST</span>
              <select value={chargeGst} onChange={e=>setChargeGst(e.target.value)} style={{ fontSize:12, width:65 }} title="GST % — 0 for exempt healthcare services (default), set a rate for a taxable line e.g. a consumable">
                {gstPercentageOptions.map(g=><option key={g} value={g}>{g}%</option>)}
              </select>
            </div>
          )}
          <button className="btn-ghost" style={{ fontSize:12, flexShrink:0, marginLeft:'auto' }} disabled={addingCharge} onClick={addCharge}>{addingCharge?'…':'+ Add charge'}</button>
        </div>

        {activeClaim && (
          <ClaimDischargeSection
            claim={activeClaim}
            runningBill={paymentRunningBill}
            splitPreview={splitPreview}
            unappliedDepositsTotal={unappliedDepositsTotal}
            isAdmin={user?.role === 'Admin'}
            claimOverrideOpen={claimOverrideOpen}
            setClaimOverrideOpen={setClaimOverrideOpen}
            claimOverrideReason={claimOverrideReason}
            setClaimOverrideReason={setClaimOverrideReason}
            printingBill={printingBill}
            onPrintBill={printMainAndBreakupBill}
            deposits={(depositsQuery.data ?? []).filter((d) => d.adjusted_bill_id == null && d.refunded_credit_note_id == null)}
            dischargeSummarySigned={structuredSummary?.status === 'signed'}
            otherOutstanding={outstanding === null ? null : outstandingTotal}
          />
        )}

        <div style={{ padding:12, background:'#F9FAFB', borderRadius:'var(--radius)' }}>
          <div style={{ marginBottom:10 }}>
            <label className="lbl">Payment mode</label>
            <select value={paymentMode} onChange={e=>setPaymentMode(e.target.value)}>
              {['Cash','UPI','Card'].map(m => <option key={m}>{m}</option>)}
            </select>
          </div>

          {outstanding === null ? (
            <div style={{ fontSize:12, color:'#9CA3AF', marginBottom:10 }}>Checking for outstanding dues…</div>
          ) : outstanding.length > 0 ? (
            <div style={{ marginBottom:12, padding:10, background:'#FEE2E2', border:'1px solid #FECACA', borderRadius:'var(--radius)' }}>
              <div style={{ fontWeight:700, fontSize:12.5, color:'#991B1B', marginBottom:6 }}>⚠ Outstanding dues — must be cleared before discharge</div>
              {outstanding.map(b => {
                const due = billDue(b);
                const total = parseFloat(String(b.total_amount)) || 0;
                const thisStay = b.admission_id != null && b.admission_id === admissionId;
                return (
                  <div key={b.id} style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#7F1D1D', marginBottom:2 }}>
                    <span>{b.bill_number} ({thisStay ? 'this admission' : b.bill_type})</span>
                    <span>
                      ₹{due.toFixed(2)}
                      {Math.abs(due - total) > 0.005 && <span style={{ opacity:0.7 }}> of ₹{total.toFixed(2)}</span>}
                    </span>
                  </div>
                );
              })}
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12.5, fontWeight:700, color:'#7F1D1D', marginTop:4, paddingTop:4, borderTop:'1px solid #FECACA' }}>
                <span>Total dues</span><span>₹{outstandingTotal.toFixed(2)}</span>
              </div>

              {!overrideOpen ? (
                <div style={{ display:'flex', gap:8, marginTop:10 }}>
                  <button className="btn-danger" style={{ padding:'6px 12px', fontSize:12 }} disabled={discharging} onClick={()=>discharge({ settle:true })}>
                    {discharging ? '…' : `Settle ₹${outstandingTotal.toFixed(2)} & discharge`}
                  </button>
                  {user?.role === 'Admin' && (
                    <button className="btn-ghost" style={{ padding:'6px 12px', fontSize:12 }} onClick={()=>setOverrideOpen(true)}>Override instead</button>
                  )}
                </div>
              ) : (
                <div style={{ marginTop:10 }}>
                  <label className="lbl">Override reason (required — e.g. insurance claim pending, LAMA)</label>
                  <textarea value={overrideReason} onChange={e=>setOverrideReason(e.target.value)} style={{ resize:'vertical', height:50, marginBottom:8 }} />
                  <div style={{ display:'flex', gap:8 }}>
                    <button className="btn-danger" style={{ padding:'6px 12px', fontSize:12 }} disabled={discharging || !overrideReason.trim()} onClick={()=>discharge({ override:true })}>
                      {discharging ? '…' : 'Discharge with dues outstanding'}
                    </button>
                    <button className="btn-ghost" style={{ padding:'6px 12px', fontSize:12 }} onClick={()=>setOverrideOpen(false)}>Back</button>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <div style={{ display:'flex', gap:8 }}>
            {outstanding !== null && outstanding.length === 0 && (!claimBlocksDischarge || claimOverrideReady) && (
              <button className="btn-danger" style={{ padding:'8px 16px' }} disabled={discharging} onClick={()=>discharge()}>
                {discharging ? 'Discharging…' : activeClaim && toCollectFromPatient != null
                  ? `Collect ₹${toCollectFromPatient.toFixed(2)} & discharge`
                  : 'Confirm discharge & bill'}
              </button>
            )}
            <button className="btn-ghost" onClick={()=>setStep('summary')}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // Step 0 ('closed'): unchanged — ongoing charge entry during the stay,
  // independent of the discharge flow, plus the button that starts it.
  return (
    <div className="card" style={{ padding:16, alignSelf:'start' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <div style={{ fontWeight:700, fontSize:15 }}>{ad.patient_name}</div>
        <button className="btn-ghost" style={{ padding: isPhone ? '5px 10px' : '3px 8px', fontSize:11 }} onClick={onClose}>{isPhone ? '← Back to wards' : '✕'}</button>
      </div>
      <div style={{ fontSize:12, color:'#6B7280', marginBottom:10 }}>
        {ad.patient_uhid} · {ad.gender}, {ad.age||'—'}y<br/>
        {ad.ward_name} · Bed {ad.bed_number} · Admitted {new Date(ad.admitted_at).toLocaleDateString('en-IN')} ({days} day{days===1?'':'s'})<br/>
        {ad.admitting_doctor && <>Admitting doctor: <b>{ad.admitting_doctor}</b><br/></>}
        {ad.provisional_diagnosis && <>Dx: {ad.provisional_diagnosis}</>}
      </div>

      {error && <div style={{ marginBottom:10, padding:'8px 12px', background:'#FEE2E2', color:'#991B1B', borderRadius:'var(--radius)', fontSize:12 }}>{error}</div>}

      <div className="mb-3 flex items-center gap-2">
        {activeClaim ? (
          <Badge variant="info" title={`Claim ${activeClaim.claim_ref} — ${activeClaim.status}`}>
            💳 {activeClaim.mode === 'cashless' ? 'Cashless' : activeClaim.mode === 'scheme' ? 'Scheme' : 'Corporate'} · {activeClaim.claim_ref}
          </Badge>
        ) : (
          <span className="text-[11px] text-ink-muted">No coverage recorded</span>
        )}
        <Button size="sm" variant="ghost" onClick={() => setShowCoveragePanel(v => !v)}>
          {showCoveragePanel ? 'Hide coverage' : activeClaim || claimForAdmission.data ? '✎ Edit coverage' : '+ Add coverage'}
        </Button>
      </div>
      {showCoveragePanel && (
        <div style={{ marginBottom:14 }}>
          <CoveragePanel
            admissionId={admissionId}
            existingClaimMode={claimForAdmission.data?.mode}
            onSaved={() => setShowCoveragePanel(false)}
          />
        </div>
      )}

      <div className="mb-3 flex items-center gap-2">
        {activePackage ? (
          <Badge variant="info" title={`${activePackage.name} (${activePackage.code})`}>
            📦 {activePackage.name}
          </Badge>
        ) : (
          <span className="text-[11px] text-ink-muted">No package applied</span>
        )}
        <Button size="sm" variant="ghost" onClick={() => setShowPackagePanel(v => !v)}>
          {showPackagePanel ? 'Hide package' : activePackage ? 'View package' : '+ Apply package'}
        </Button>
      </div>
      {showPackagePanel && (
        <div style={{ marginBottom:14 }}>
          {/* Applying/removing a package changes two things this screen
              already shows elsewhere and must refresh in place: the
              package's own 'package'-head ip_charges line (in the Charges
              list / running bill below — `load()` re-reads the admission,
              which the running-bill effect is already keyed on) and the
              bed-board chip (`onChanged` === the parent's `loadWards`,
              same callback discharge already uses for the same reason:
              the board's own `wards` state is only fetched once on mount). */}
          <PackagePanel admissionId={admissionId} onChanged={() => { load(); onChanged?.(); }} />
        </div>
      )}

      <ChargesList ad={ad} days={days} roomEstimate={roomEstimate} runningSubtotal={runningSubtotal} runningGst={runningGst}
        runningTotal={runningTotal} runningBillStatement={runningBillStatement} gstEnabled={gstEnabled} gstPercentageOptions={gstPercentageOptions}
        roomRentGst={roomRentGst} setRoomRentGst={setRoomRentGst} removeCharge={removeCharge} />

      <div style={{ display:'grid', gridTemplateColumns:'110px 1fr', gap:6, marginBottom:6 }}>
        <select value={chargeType} onChange={e=>setChargeType(e.target.value)} style={{ fontSize:12 }}>
          {CHARGE_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <input placeholder="Description" value={chargeDesc} onChange={e=>setChargeDesc(e.target.value)} style={{ fontSize:12 }} />
      </div>
      <div style={{ display:'flex', gap:6, marginBottom:14 }}>
        <input type="number" placeholder="Amount ₹" value={chargeAmount} onChange={e=>setChargeAmount(e.target.value)} style={{ fontSize:12 }} />
        <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
          <span style={{ fontSize:10, fontWeight:600, color:'#9CA3AF' }}>Disc</span>
          <input
            type="number" min="0" max="100" value={chargeDiscount} onChange={e=>setChargeDiscount(e.target.value)}
            style={{ fontSize:12, width:50 }} title="Discount % — a courtesy discount on this specific charge line"
          />
        </div>
        {gstEnabled && (
          <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
            <span style={{ fontSize:10, fontWeight:600, color:'#9CA3AF' }}>GST</span>
            <select value={chargeGst} onChange={e=>setChargeGst(e.target.value)} style={{ fontSize:12, width:65 }} title="GST % — 0 for exempt healthcare services (default), set a rate for a taxable line e.g. a consumable">
              {gstPercentageOptions.map(g=><option key={g} value={g}>{g}%</option>)}
            </select>
          </div>
        )}
        <button className="btn-ghost" style={{ fontSize:12, flexShrink:0 }} disabled={addingCharge} onClick={addCharge}>{addingCharge?'…':'+ Add charge'}</button>
      </div>

      <button className="btn-primary" style={{ width:'100%' }} onClick={startDischarge}>🏁 Discharge patient</button>
    </div>
  );
}

// Insurance Claims Phase 4.5.5 (wireframe S5) — the claim gate, the
// head-wise split preview and the "Collect from patient" panel, shown only
// in the 'payment' step and only for an admission carrying an active
// cashless/scheme/corporate claim. Split off from AdmissionDetail's render
// purely to keep that function's JSX readable — all of its state stays in
// the parent (this panel is otherwise stateless).
function ClaimDischargeSection({
  claim, runningBill, splitPreview, unappliedDepositsTotal, isAdmin,
  claimOverrideOpen, setClaimOverrideOpen, claimOverrideReason, setClaimOverrideReason,
  printingBill, onPrintBill, deposits, dischargeSummarySigned, otherOutstanding,
}: {
  claim: import('../features/claims/types').Claim;
  runningBill: IpRunningBill | null;
  splitPreview: SplitPreviewResult | null;
  unappliedDepositsTotal: number;
  isAdmin: boolean;
  claimOverrideOpen: boolean;
  setClaimOverrideOpen: (v: boolean) => void;
  claimOverrideReason: string;
  setClaimOverrideReason: (v: string) => void;
  printingBill: boolean;
  onPrintBill: () => void;
  deposits: Array<{ id: number; receipt_no: string; amount: number | string }>;
  dischargeSummarySigned: boolean;
  otherOutstanding: number | null;
}) {
  const blocked = claim.status !== 'final_approved';

  if (blocked) {
    return (
      <div className="mb-3 rounded-xl border border-warn/40 bg-warn/10 p-3">
        <div className="mb-1 text-[12.5px] font-semibold text-warn-ink">
          ⚠ Claim {claim.claim_ref} is not yet final_approved (currently '{claim.status}')
        </div>
        <div className={`text-xs text-warn-ink ${isAdmin ? 'mb-2' : ''}`}>
          Get final authorisation from the Claims desk first{isAdmin ? ', or override below.' : '.'}
        </div>
        {isAdmin && (
          !claimOverrideOpen ? (
            <Button size="sm" variant="ghost" onClick={() => setClaimOverrideOpen(true)}>
              Override & discharge anyway
            </Button>
          ) : (
            <div>
              <label className="lbl">Override reason (required)</label>
              <Textarea value={claimOverrideReason} onChange={e => setClaimOverrideReason(e.target.value)} rows={2} />
            </div>
          )
        )}
      </div>
    );
  }

  if (!splitPreview || !runningBill) {
    return <div className="mb-3 text-xs text-ink-muted">Loading claim split preview…</div>;
  }

  const toCollect = Math.max(0, round2(splitPreview.patientShare - unappliedDepositsTotal));

  // Wireframe S5's two-card grid (Final statement | Collect from patient) —
  // same layout as ClaimWorkspace's Overview tab, reused here rather than
  // inventing a third variant of the same "statement + collect" recipe.
  return (
    <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
      <Section title="Final statement — insurer / patient split (preview)">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Head</TableHead>
              <TableHead className="text-right">Net</TableHead>
              <TableHead className="text-right">Insurer</TableHead>
              <TableHead className="text-right">Patient</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {splitPreview.heads.map(h => (
              <TableRow key={h.head}>
                <TableCell>{h.head}</TableCell>
                <TableCell className="text-right tabular-nums">₹{h.net.toFixed(2)}</TableCell>
                <TableCell className="text-right tabular-nums">₹{h.payerAmount.toFixed(2)}</TableCell>
                <TableCell className="text-right tabular-nums">₹{h.patientAmount.toFixed(2)}</TableCell>
              </TableRow>
            ))}
            <TableRow className="font-semibold">
              <TableCell>Total</TableCell>
              <TableCell className="text-right tabular-nums">₹{splitPreview.statementNet.toFixed(2)}</TableCell>
              <TableCell className="text-right tabular-nums">₹{splitPreview.insurerShare.toFixed(2)}</TableCell>
              <TableCell className="text-right tabular-nums">₹{splitPreview.patientShare.toFixed(2)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <div className="mt-2 text-[10.5px] text-ink-muted">
          Preview only — the head-wise split above is an estimate; the real split is computed and written
          when discharge is confirmed. The totals row is exact.
        </div>
      </Section>

      <Section title="Collect from patient">
        <div className="flex justify-between text-[12.5px]">
          <span>Patient share</span><span className="tabular-nums">₹{splitPreview.patientShare.toFixed(2)}</span>
        </div>
        {deposits.length > 0 ? deposits.map(d => (
          <div key={d.id} className="mt-1 flex justify-between text-[12.5px]">
            <span>Less deposit {d.receipt_no}</span><span className="tabular-nums">− ₹{Number(d.amount).toFixed(2)}</span>
          </div>
        )) : unappliedDepositsTotal > 0 && (
          <div className="mt-1 flex justify-between text-[12.5px]">
            <span>Less deposit(s) on file</span><span className="tabular-nums">− ₹{unappliedDepositsTotal.toFixed(2)}</span>
          </div>
        )}
        <div className="mt-2 flex justify-between border-t border-dashed border-x-0 border-b-0 border-border pt-2 font-semibold">
          <span className="text-sm">To collect now</span><span className="text-base tabular-nums">₹{toCollect.toFixed(2)}</span>
        </div>
        <div className="mt-1 flex justify-between text-[12px] text-ink-muted">
          <span>Other outstanding (OP / pharmacy, patient share)</span>
          <span className="tabular-nums">{otherOutstanding == null ? '…' : `₹${otherOutstanding.toFixed(2)}`}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Badge variant="success">✔ Final authorisation ₹{Number(claim.final_approved ?? claim.approved_total ?? 0).toFixed(0)}</Badge>
          <Badge variant={dischargeSummarySigned ? 'success' : 'warn'}>{dischargeSummarySigned ? '✔ Discharge summary signed' : '! Discharge summary not signed'}</Badge>
        </div>
        <div className="mt-2 text-[10.5px] text-success">
          Insurer share ₹{splitPreview.insurerShare.toFixed(2)} → receivable across {runningBill.invoiceIds.length || 1} invoice(s) once discharged.
        </div>
        <Button size="sm" variant="ghost" className="mt-2" disabled={printingBill} onClick={onPrintBill}>
          {printingBill ? 'Rendering…' : '📄 Print Main Bill + Breakup'}
        </Button>
      </Section>
    </div>
  );
}

// Shared by the ongoing-charges view (step 'closed') and the final review in
// step 'payment' — same rows, same running total, just shown at two
// different points in the same admission's lifecycle.
function ChargesList({ ad, days, roomEstimate, runningSubtotal, runningGst, runningTotal, runningBillStatement, gstEnabled, gstPercentageOptions, roomRentGst, setRoomRentGst, removeCharge }: {
  ad: Admission; days: number; roomEstimate: number; runningSubtotal: number; runningGst: number; runningTotal: number;
  runningBillStatement: { net: number } | null;
  gstEnabled: boolean; gstPercentageOptions: (number | string)[];
  roomRentGst: string; setRoomRentGst: (v: string) => void; removeCharge: (chargeId: number) => void;
}) {
  return (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
        <div style={{ fontSize:11.5, fontWeight:700, textTransform:'uppercase', color:'#6B7280' }}>Charges so far</div>
        {runningBillStatement != null && (
          <span
            title="Live claims running bill (utils/finalBill.js) — every charge and bill for this admission so far, head-wise"
            style={{ fontSize:11, fontWeight:600, color:'#0369A1', background:'#E0F2FE', borderRadius:999, padding:'2px 8px' }}
          >
            Running bill: ₹{Number(runningBillStatement.net).toFixed(2)}
          </span>
        )}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:4, marginBottom:8 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:12, padding:'4px 8px', background:'#F9FAFB', borderRadius:6, fontStyle:'italic', color:'#6B7280' }}>
          <span>Room rent (est., {days}d × ₹{ad.daily_rate})</span>
          <span style={{ display:'flex', alignItems:'center', gap:6 }}>
            {gstEnabled && (
              <span style={{ display:'flex', alignItems:'center', gap:4, fontStyle:'normal' }}>
                <span style={{ fontSize:10, fontWeight:600, color:'#9CA3AF' }}>GST</span>
                <select value={roomRentGst} onChange={e=>setRoomRentGst(e.target.value)} style={{ fontSize:11, padding:'1px 3px', width:65 }} title="Room rent GST % — applied to the room rent line at discharge">
                  {gstPercentageOptions.map(g=><option key={g} value={g}>{g}%</option>)}
                </select>
              </span>
            )}
            ₹{roomEstimate.toFixed(2)}
          </span>
        </div>
        {ad.charges.map(c => (
          <div key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:12, padding:'4px 8px', background:'#F9FAFB', borderRadius:6 }}>
            <span>{c.description} {c.quantity>1 ? `×${c.quantity}` : ''}</span>
            <span style={{ display:'flex', alignItems:'center', gap:6 }}>₹{(parseFloat(String(c.amount))*c.quantity).toFixed(2)} {c.lab_order_id
              ? <span style={{ fontSize:10, color:'#9CA3AF' }} title="Laboratory test charge — cancel the test in the Laboratory module to reverse it">🧪</span>
              : <button className="btn-ghost" style={{ padding:'1px 6px', fontSize:10 }} onClick={()=>removeCharge(c.id)}>✕</button>}</span>
          </div>
        ))}
        {ad.charges.length === 0 && <div style={{ fontSize:11.5, color:'#9CA3AF' }}>No ad-hoc charges added yet.</div>}
      </div>
      <div style={{ paddingTop:6, borderTop:'1px dashed #E5E7EB', marginBottom:12 }}>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#6B7280', marginBottom: gstEnabled ? 2 : 0 }}>
          <span>Subtotal (incl. room est.)</span><span>₹{runningSubtotal.toFixed(2)}</span>
        </div>
        {gstEnabled && (
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#6B7280', marginBottom:2 }}>
            <span>GST</span><span>₹{runningGst.toFixed(2)}</span>
          </div>
        )}
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:12.5, fontWeight:700 }}>
          <span>Running total</span><span>₹{runningTotal.toFixed(2)}</span>
        </div>
      </div>
    </>
  );
}
